import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import CreateTaskDialog from "./CreateTaskDialog";
import { API_BASE, formatApiError } from "./AuthContext";
import {
  Mail, StickyNote, GitBranch, FileText, Receipt, CheckSquare, Upload,
  PenLine, Clock, Share2, Phone, MessageCircle, ArrowDownLeft, ArrowUpRight,
  Users, Activity, Plus, X, Save, Loader2, Pin, PinOff, CalendarDays, MailOpen, ShieldCheck, ListPlus,
} from "lucide-react";

const META = {
  communication: { label: "Comms", icon: Mail, color: "var(--accent)" },
  note: { label: "Notes", icon: StickyNote, color: "#B45309" },
  status: { label: "Status", icon: Activity, color: "#0891B2" },
  job: { label: "Jobs", icon: GitBranch, color: "#1D4ED8" },
  document: { label: "Documents", icon: FileText, color: "#0F766E" },
  invoice: { label: "Invoices", icon: Receipt, color: "#15803D" },
  task: { label: "Tasks", icon: CheckSquare, color: "#7C3AED" },
  file_request: { label: "Requests", icon: Upload, color: "#C2410C" },
  esign: { label: "E-sign", icon: PenLine, color: "#9333EA" },
  time: { label: "Time", icon: Clock, color: "#0369A1" },
  referral: { label: "Referrals", icon: Share2, color: "#BE185D" },
  identity: { label: "ID check", icon: ShieldCheck, color: "#0F766E" },
  manual: { label: "Logged", icon: Phone, color: "#4B5563" },
};

function eventIcon(e) {
  if (e.kind === "email_opened") return MailOpen;
  if (e.category === "communication") {
    if (e.kind === "call") return Phone;
    if (e.kind === "sms") return MessageCircle;
    return e.direction === "inbound" ? ArrowDownLeft : ArrowUpRight;
  }
  if (e.category === "manual") {
    if (e.kind === "meeting") return Users;
    if (e.kind === "call") return Phone;
    if (e.kind === "email") return Mail;
    return StickyNote;
  }
  return (META[e.category] || META.note).icon;
}

function Chip({ active, onClick, label, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
        active ? "bg-[var(--ink)] text-white border-[var(--ink)]"
               : "bg-white text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--ink)]"}`}
      style={!active && color ? { color } : undefined}>
      {label}
    </button>
  );
}

export default function ActivityTimeline({ scope, id }) {
  const base = scope === "lead" ? "leads" : "clients";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [taskFor, setTaskFor] = useState(null);
  const [jobFilter, setJobFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    if (scope !== "client") return;
    axios.get(`${API_BASE}/jobs`, { params: { client_id: id } }).then((r) => setJobs(r.data || [])).catch(() => {});
  }, [scope, id]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (jobFilter) params.job_id = jobFilter;
    axios.get(`${API_BASE}/${base}/${id}/activity`, { params })
      .then(({ data: d }) => setData(d))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [base, id, from, to, jobFilter]);

  useEffect(() => { load(); }, [load]);

  const allEvents = data?.events || [];
  const presentCats = Array.from(new Set(allEvents.map((e) => e.category)));
  const events = filter === "all" ? allEvents : allEvents.filter((e) => e.category === filter);

  const togglePin = async (e) => {
    try {
      await axios.post(`${API_BASE}/activity/pin`, {
        scope, scope_id: id, event_key: e.event_key, pinned: !e.pinned,
      });
      setData((d) => ({ ...d, events: d.events.map((x) =>
        x.event_key === e.event_key ? { ...x, pinned: !e.pinned } : x) }));
      toast.success(e.pinned ? "Unpinned" : "Pinned to top");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const when = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-5" data-testid={`${scope}-activity-panel`}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-[10px]">
          {allEvents.length} activit{allEvents.length === 1 ? "y" : "ies"}
        </div>
        <button onClick={() => setShowLog(true)} data-testid={`${scope}-log-activity-btn`}
          className="inline-flex items-center gap-1 text-[11px] bg-[var(--ink)] text-white rounded px-2.5 py-1.5 hover:opacity-90">
          <Plus size={12} /> Log activity
        </button>
      </div>

      {/* Date range */}
      <div className="flex items-center flex-wrap gap-2 mb-3" data-testid={`${scope}-activity-daterange`}>
        <CalendarDays size={13} className="text-[var(--ink-3)]" />
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          data-testid={`${scope}-activity-date-from`}
          className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]" />
        <span className="text-[11px] text-[var(--ink-3)]">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid={`${scope}-activity-date-to`}
          className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]" />
        {(from || to) && (
          <button onClick={() => { setFrom(""); setTo(""); }} data-testid={`${scope}-activity-date-clear`}
            className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-0.5">
            <X size={11} /> Clear
          </button>
        )}
        {scope === "client" && jobs.length > 0 && (
          <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} data-testid="client-activity-job-filter"
            className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] max-w-[220px] focus:outline-none focus:border-[var(--ink)]">
            <option value="">All jobs</option>
            {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
          </select>
        )}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`${scope}-activity-filters`}>
        <Chip active={filter === "all"} onClick={() => setFilter("all")} label={`All (${allEvents.length})`} />
        {presentCats.map((c) => {
          const m = META[c] || { label: c };
          const n = allEvents.filter((e) => e.category === c).length;
          return (
            <Chip key={c} active={filter === c} onClick={() => setFilter(c)}
              label={`${m.label} (${n})`} color={m.color} testid={`${scope}-activity-filter-${c}`} />
          );
        })}
      </div>

      {loading && !data ? (
        <div className="text-[12px] text-[var(--ink-3)] inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading activity…
        </div>
      ) : err ? (
        <div className="text-[12px] text-[#B91C1C]">{err}</div>
      ) : events.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic">
          Nothing here yet. Emails, {scope === "lead" ? "status changes, referrals" : "jobs, invoices, e-signatures"} and
          anything you log will appear on this timeline with timestamps.
        </div>
      ) : (
        <div className="relative" data-testid={`${scope}-activity-list`}>
          {events.map((e, i) => {
            const m = META[e.category] || META.note;
            const Icon = eventIcon(e);
            const opened = e.meta?.opened_at;
            return (
              <div key={e.event_key || i}
                   className={`group flex items-start gap-3 py-2.5 border-b border-[var(--border)] ${e.pinned ? "bg-[var(--subtle)]/60 -mx-2 px-2 rounded" : ""}`}
                   data-testid={`${scope}-activity-event-${i}`}>
                <div className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
                     style={{ background: "var(--subtle)", color: m.color }}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)] flex-wrap">
                    {e.pinned && <Pin size={10} className="text-[var(--accent)]" />}
                    <span className="uppercase tracking-wider font-medium" style={{ color: m.color }}>{e.title}</span>
                    <span>·</span>
                    <span data-testid={`${scope}-activity-time-${i}`}>{when(e.at)}</span>
                    {e.actor_name && <span>· {e.actor_name}</span>}
                    {opened && e.kind === "email" && (
                      <span className="inline-flex items-center gap-0.5 text-[var(--moss)]">
                        · <MailOpen size={10} /> opened{e.meta?.open_count > 1 ? ` ×${e.meta.open_count}` : ""}
                      </span>
                    )}
                  </div>
                  {e.detail && <div className="text-[13px] text-[var(--ink)] mt-0.5 break-words">{e.detail}</div>}
                  {e.meta?.snippet && <div className="text-[11px] text-[var(--ink-2)] mt-1 line-clamp-2">{e.meta.snippet}</div>}
                  {e.meta?.note && <div className="text-[11px] text-[var(--ink-2)] mt-1 whitespace-pre-wrap">{e.meta.note}</div>}
                  {e.category === "communication" && (e.meta?.from || e.meta?.to) && e.kind !== "email_opened" && (
                    <div className="text-[10px] text-[var(--ink-3)] font-mono mt-1 truncate">
                      {e.direction === "inbound" ? `from ${e.meta.from || ""}` : `to ${e.meta.to || ""}`}
                    </div>
                  )}
                </div>
                <button onClick={() => setTaskFor(e)} data-testid={`${scope}-activity-task-${i}`}
                  title="Create a task from this activity"
                  className="flex-shrink-0 text-[var(--ink-3)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-[var(--accent)]">
                  <ListPlus size={13} />
                </button>
                <button onClick={() => togglePin(e)} data-testid={`${scope}-activity-pin-${i}`}
                  title={e.pinned ? "Unpin" : "Pin to top"}
                  className={`flex-shrink-0 ${e.pinned ? "text-[var(--accent)]" : "text-[var(--ink-3)] opacity-0 group-hover:opacity-100"} hover:text-[var(--accent)]`}>
                  {e.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {taskFor && (
        <CreateTaskDialog
          defaults={{ title: `Follow up: ${taskFor.title}${taskFor.detail ? ` — ${taskFor.detail}` : ""}`.slice(0, 240),
            description: [taskFor.detail, taskFor.meta?.snippet, taskFor.meta?.note].filter(Boolean).join("\n\n").slice(0, 4000),
            parent_kind: scope, parent_id: id,
            source: { type: "activity", id: taskFor.event_key || `${taskFor.kind}:${taskFor.at}`, label: `${taskFor.title} · ${when(taskFor.at)}`.slice(0, 300) } }}
          context={`${taskFor.title} (${when(taskFor.at)})`}
          onClose={() => setTaskFor(null)} onCreated={() => setTaskFor(null)} />
      )}

      {showLog && (
        <LogActivityModal scope={scope} id={id} onClose={() => setShowLog(false)}
          onSaved={() => { setShowLog(false); load(); }} />
      )}
    </div>
  );
}

function LogActivityModal({ scope, id, onClose, onSaved }) {
  const base = scope === "lead" ? "leads" : "clients";
  const [type, setType] = useState("call");
  const [summary, setSummary] = useState("");
  const [detail, setDetail] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [saving, setSaving] = useState(false);
  const TYPES = [
    { key: "call", label: "Call", icon: Phone },
    { key: "meeting", label: "Meeting", icon: Users },
    { key: "note", label: "Note", icon: StickyNote },
    { key: "email", label: "Email", icon: Mail },
    { key: "other", label: "Other", icon: Activity },
  ];

  const save = async () => {
    if (!summary.trim()) { toast.error("Add a short summary"); return; }
    setSaving(true);
    try {
      const body = { type, summary: summary.trim(), detail: detail.trim() || null };
      if (occurredAt) body.occurred_at = new Date(occurredAt).toISOString();
      await axios.post(`${API_BASE}/${base}/${id}/activity`, body);
      toast.success("Activity logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="log-activity-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div className="text-[13px] font-semibold">Log activity</div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="overline text-[10px] mb-1.5">Type</div>
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((t) => {
                const I = t.icon;
                return (
                  <button key={t.key} onClick={() => setType(t.key)}
                    data-testid={`log-activity-type-${t.key}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded border ${
                      type === t.key ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                     : "bg-white text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--ink)]"}`}>
                    <I size={12} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline text-[10px] mb-1.5">Summary</div>
            <input value={summary} onChange={(e) => setSummary(e.target.value)}
              data-testid="log-activity-summary" autoFocus
              placeholder="e.g. Called to confirm BAS figures"
              className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <div>
            <div className="overline text-[10px] mb-1.5">Details (optional)</div>
            <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
              data-testid="log-activity-detail" rows={3}
              placeholder="What was discussed, decisions, next steps…"
              className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
          </div>
          <div>
            <div className="overline text-[10px] mb-1.5">When (optional)</div>
            <input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)}
              data-testid="log-activity-occurred-at"
              className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
            <div className="text-[10px] text-[var(--ink-3)] mt-1">Leave blank to use now.</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[var(--border)]">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 text-[var(--ink-2)] hover:text-[var(--ink)]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="log-activity-save"
            className="inline-flex items-center gap-1 text-[12px] bg-[var(--ink)] text-white rounded px-3 py-1.5 hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
